<script lang="ts">
	import { uniqBy } from 'es-toolkit/array'
	import { AreaChart, LineChart } from 'layerchart'

	import ChartFrame from '#lib/components/chart-frame.svelte'
	import * as Chart from '#lib/components/ui/chart/index.js'
	import type { LineChartRecord, ChainData } from '#lib/models/index.js'
	import {
		getValueAxisPadding,
		X_AXIS_LABELS_HEIGHT,
		X_AXIS_PROPS,
		Y_AXIS_PROPS,
	} from '#lib/utils/chart.js'
	import { LINE_CHART_METRIC_CONFIG, type LineChartMetric } from '#lib/utils/lineChartMetrics.js'
	import { n } from '#lib/utils/navigation.svelte.js'

	let {
		chains,
		chartData,
		metric,
	}: {
		chains: ChainData[]
		chartData: LineChartRecord[]
		metric: LineChartMetric
	} = $props()

	const COLORS = ['#027dfd', '#1be6ab']
	const MAX_X_TICKS = 8
	const LEGEND_HEIGHT = 14

	const config = $derived(LINE_CHART_METRIC_CONFIG[metric])

	const getColor = (index: number) =>
		chains.length === 1
			? COLORS[config.category === 'scalability' || config.category === 'dev-activity' ? 0 : 1]
			: COLORS[index % COLORS.length]

	const tooltipDateFormat = new Intl.DateTimeFormat('en-US', {
		day: 'numeric',
		month: 'short',
		timeZone: 'UTC',
		timeZoneName: 'short',
		weekday: 'short',
	})

	const tickDateFormat = new Intl.DateTimeFormat('en-US', {
		month: 'short',
		day: 'numeric',
		timeZone: 'UTC',
	})

	const yTickFormat = (value: number) => (config.tickFormat ?? config.format)(value, chains[0])

	const xTickFormat = (date: Date) => tickDateFormat.format(date)

	const series = $derived(
		chains.map((chain, i) => ({
			key: chain.id,
			label: chain.name,
			value: (d: LineChartRecord) => d.ys[i],
			color: getColor(i),
		}))
	)

	const chartConfig = $derived<Chart.ChartConfig>(
		Object.fromEntries(series.map(({ key, label, color }) => [key, { label, color }]))
	)

	const xTicks = $derived.by(() => {
		const dates = uniqBy(
			chartData.map(({ x }) => x),
			xTickFormat
		)
		const step = Math.ceil(dates.length / MAX_X_TICKS)

		return dates.filter((_, i) => i % step === 0)
	})

	const chartProps = $derived({
		xAxis: { ...X_AXIS_PROPS, format: xTickFormat, ticks: xTicks },
		yAxis: { ...Y_AXIS_PROPS, format: yTickFormat },
		area: { line: { strokeWidth: 2 } },
		// explicit opacity stops layerchart fading the other lines when a dot is hovered
		spline: { strokeWidth: 2, opacity: 1 },
	})

	const maxValue = $derived(
		Math.max(0, ...chartData.flatMap(({ ys }) => ys).filter(value => value != null))
	)
	const yDomain = $derived<[number, number]>([0, maxValue * 1.1])

	const padding = $derived({
		top: 12,
		// room for the last x tick label, centered on the right edge
		right: 24,
		bottom: X_AXIS_LABELS_HEIGHT + (chains.length > 1 ? LEGEND_HEIGHT : 0),
		left: getValueAxisPadding(yDomain, yTickFormat),
	})
</script>

<ChartFrame
	class={chains.length > 1 ? 'h-82.5' : 'h-75'}
	empty={chartData.length === 0}
	loading={n.l}
>
	{#if chartData.length > 0}
		<Chart.Container class="aspect-auto h-full w-full" config={chartConfig}>
			{const ChartType = $derived(chains.length > 1 ? LineChart : AreaChart)}
			<ChartType
				data={chartData}
				grid={false}
				legend={chains.length > 1}
				{padding}
				props={chartProps}
				{series}
				x="x"
				{yDomain}
				yNice={chains.length > 1}
			>
				{#snippet tooltip()}
					<Chart.Tooltip
						hideIndicator={chains.length === 1}
						hideName={chains.length === 1}
						labelClassName="text-muted-foreground font-normal"
						labelFormatter={(_, payload) => {
							const date = payload[0]?.payload?.x
							return date instanceof Date ? tooltipDateFormat.format(date) : ''
						}}
						sort={(a, b) => b.value - a.value}
						valueFormatter={(value, item) =>
							config.format(value, chains.find(({ id }) => id === item.key) ?? chains[0])}
					/>
				{/snippet}
			</ChartType>
		</Chart.Container>
	{/if}
</ChartFrame>
